<script setup lang="ts">
/**
 * One-click GNK top-up via GonkaKeeper.
 *
 * Orchestration (the backend prepares + broadcasts; the frontend only signs):
 *   1. enable() + getKey() on the cached provider.
 *   2. Validate the returned bech32 prefix matches the hub's chain.
 *   3. POST /api/topups/prepare-tx  → { signDoc, accountNumber, sequence, chainId }
 *   4. provider.signAmino(chainId, sender, signDoc) → { signed, signature }
 *   5. POST /api/topups/broadcast-tx → { txHash, code, rawLog }
 *   6. Emit success(txHash, amountNgonka).
 *
 * We never call provider.sendTx — broadcast goes through the backend for
 * CORS safety and audit.
 */
import { computed, ref } from 'vue';
import { ApiError } from '../api/client';
import { topupApi, type DepositInfo } from '../api/topup';

interface Props {
  depositInfo: DepositInfo;
  provider: GonkaProvider;
  chainId: string;
  bech32Prefix: string;
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'success', txHash: string, amountNgonka: string): void;
}>();

const NGONKA_PER_GNK = 1_000_000_000n;
const PRESETS_GNK = [1, 5, 10, 50] as const;

const amountGnk = ref<number>(5);
const busy = ref(false);
const error = ref<string | null>(null);
const errorDetail = ref<string | null>(null);
const detailOpen = ref(false);

const minNgonka = computed(() => BigInt(props.depositInfo.minNgonka));
const minGnkDisplay = computed(() => Number(minNgonka.value) / Number(NGONKA_PER_GNK));

const amountNgonka = computed<string | null>(() => {
  const v = amountGnk.value;
  if (!Number.isFinite(v) || v <= 0) return null;
  // Convert via integer arithmetic to avoid float drift on, e.g., 0.1 GNK.
  // 9 decimals (GNK→ngonka). Reject values with > 9 decimal places.
  const [intPart, fracPart = ''] = String(v).split('.');
  if (fracPart.length > 9) return null;
  const frac = (fracPart + '0'.repeat(9)).slice(0, 9);
  const combined = `${intPart}${frac}`.replace(/^0+/, '') || '0';
  if (!/^[1-9][0-9]*$/.test(combined)) return null;
  return combined;
});

const amountTooSmall = computed(() => {
  const a = amountNgonka.value;
  if (!a) return false;
  return BigInt(a) < minNgonka.value;
});

const canPay = computed(
  () => !busy.value && amountNgonka.value !== null && !amountTooSmall.value,
);

function setPreset(gnk: number): void {
  amountGnk.value = gnk;
}

function clearError(): void {
  error.value = null;
  errorDetail.value = null;
  detailOpen.value = false;
}

async function handlePay(): Promise<void> {
  if (!canPay.value || amountNgonka.value === null) return;
  busy.value = true;
  clearError();
  try {
    // 1. Authorize this site for the chain (no re-auth, just permission).
    await props.provider.enable(props.chainId);
    const key = await props.provider.getKey(props.chainId);

    // 2. Hub uses the gonka1… address space; reject other-prefix wallets here
    //    instead of letting the chain reject with an obscure code.
    if (!key.bech32Address.startsWith(`${props.bech32Prefix}1`)) {
      throw new Error(
        `Wallet returned address with wrong prefix. Expected "${props.bech32Prefix}1…", got "${key.bech32Address.slice(0, 12)}…".`,
      );
    }

    // 3. Backend prepares the StdSignDoc + memo bound to the logged-in user.
    const prepared = await topupApi.prepareTx({
      senderAddress: key.bech32Address,
      amountNgonka: amountNgonka.value,
    });

    // 4. Keeper popup — user reviews the JSON and clicks Approve.
    const signResult = await props.provider.signAmino(
      prepared.chainId,
      key.bech32Address,
      prepared.signDoc,
    );

    // 5. Backend assembles TxRaw and forwards to Tendermint /broadcast_tx_sync.
    const broadcast = await topupApi.broadcastTx({
      signed: signResult.signed,
      signature: signResult.signature,
    });

    if (broadcast.code !== 0) {
      error.value = 'Транзакция отклонена сетью';
      errorDetail.value = broadcast.rawLog || `code=${broadcast.code}`;
      return;
    }

    emit('success', broadcast.txHash, amountNgonka.value);
  } catch (err) {
    handleError(err);
  } finally {
    busy.value = false;
  }
}

function handleError(err: unknown): void {
  if (err instanceof ApiError) {
    if (err.code === 'network_error') {
      error.value = 'Ошибка сети, попробуйте ещё раз';
    } else if (err.status === 400 || err.status === 422) {
      error.value = err.message;
    } else if (err.status >= 500) {
      error.value = 'Транзакция отклонена сетью';
      errorDetail.value = err.message;
    } else {
      error.value = err.message;
    }
    return;
  }
  const msg = (err as Error).message || 'Неизвестная ошибка';
  // Keeper signals user rejection via free-form messages — match loosely.
  if (/reject|denied|cancel|отмен/i.test(msg)) {
    error.value = 'Подпись отменена';
    return;
  }
  error.value = msg;
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center px-4"
    @click.self="emit('close')"
  >
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/70" />

    <div class="relative card p-6 max-w-md w-full">
      <div class="flex items-start justify-between mb-4">
        <h2 class="text-base font-semibold">Pay with GonkaKeeper</h2>
        <button
          class="btn-ghost text-xs"
          type="button"
          :disabled="busy"
          @click="emit('close')"
        >
          ✕
        </button>
      </div>

      <div class="flex flex-col gap-4">
        <div>
          <label class="text-xs uppercase tracking-wide text-muted">Amount (GNK)</label>
          <input
            v-model.number="amountGnk"
            type="number"
            min="0"
            step="0.000000001"
            :disabled="busy"
            class="mt-2 w-full bg-code border border-border rounded px-3 py-2 text-sm"
          />
        </div>

        <div class="flex gap-2 flex-wrap">
          <button
            v-for="p in PRESETS_GNK"
            :key="p"
            type="button"
            class="btn-ghost text-xs"
            :disabled="busy"
            @click="setPreset(p)"
          >
            {{ p }} GNK
          </button>
        </div>

        <div class="text-xs text-muted">
          Minimum: {{ minGnkDisplay }} GNK ({{ depositInfo.minNgonka }} ngonka)
        </div>

        <div v-if="amountTooSmall" class="text-xs text-warn">
          Amount is below the minimum top-up.
        </div>

        <div v-if="error" class="text-xs text-danger flex flex-col gap-1">
          <span>{{ error }}</span>
          <button
            v-if="errorDetail"
            type="button"
            class="text-left underline"
            @click="detailOpen = !detailOpen"
          >
            {{ detailOpen ? 'Hide details' : 'Show details' }}
          </button>
          <pre
            v-if="errorDetail && detailOpen"
            class="bg-code border border-border rounded p-2 text-[10px] whitespace-pre-wrap break-all"
          >{{ errorDetail }}</pre>
          <button
            v-if="!busy"
            type="button"
            class="btn-ghost text-xs self-start mt-1"
            @click="handlePay"
          >
            Retry
          </button>
        </div>

        <button
          class="btn-primary w-full"
          :disabled="!canPay"
          type="button"
          @click="handlePay"
        >
          {{ busy ? 'Signing…' : '⚡ Pay' }}
        </button>
      </div>
    </div>
  </div>
</template>
